<script>
export default { name: 'UiModal' };
</script>

<script setup>
import { ref, computed } from 'vue';
import { X } from 'lucide-vue-next';
import { useModalA11y } from '@/composables/useModalA11y';

const props = defineProps({
  show: { type: Boolean, required: true },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  // sm | md | lg | xl | 2xl | 3xl | 4xl | 5xl | 6xl  (mapa a max-w-*; default 3xl = max-w-4xl)
  size: { type: String, default: '3xl' },
  // Anula completamente el ancho, ej. widthClass="max-w-6xl"
  widthClass: { type: String, default: null },
  closable: { type: Boolean, default: true },
  closeOnBackdrop: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const rootRef = ref(null);
useModalA11y(rootRef, () => props.show, () => emit('close'));

const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
  '2xl': 'max-w-3xl',
  '3xl': 'max-w-4xl',
  '4xl': 'max-w-5xl',
  '5xl': 'max-w-6xl',
  '6xl': 'max-w-7xl',
};

const panelClass = computed(() => [
  'ui-modal-panel bg-white dark:bg-slate-900 w-full rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]',
  props.widthClass || SIZES[props.size] || SIZES['3xl'],
]);
</script>

<template>
  <!-- Teleport a <body>: evita que el overlay `fixed` quede atrapado por ancestros con
       transform/filter/overflow (p. ej. wrappers físicos con animate-fade-in scoped). -->
  <Teleport to="body">
    <Transition name="ui-modal">
    <div
      v-if="show"
      ref="rootRef"
      role="dialog"
      aria-modal="true"
      :aria-label="title || 'Ventana modal'"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      @click.self="closeOnBackdrop && emit('close')"
    >
      <div :class="panelClass">
        <!-- CABECERA (opcional) -->
        <div
          v-if="title || subtitle || $slots.header"
          class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30 gap-4 shrink-0"
        >
          <div class="min-w-0">
            <slot name="header">
              <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                {{ title }}
              </h2>
              <p v-if="subtitle" class="text-xs text-slate-500">{{ subtitle }}</p>
            </slot>
          </div>
          <button
            v-if="closable"
            type="button"
            aria-label="Cerrar"
            title="Cerrar"
            class="p-2 shrink-0 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500"
            @click="emit('close')"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- TOOLBAR (opcional) — barra fija entre cabecera y cuerpo (ej. tabs, filtros rápidos) -->
        <div v-if="$slots.toolbar" class="shrink-0">
          <slot name="toolbar" />
        </div>

        <!-- CUERPO
             `min-h-0` es imprescindible: sin él, un hijo flex con overflow tiene
             min-height:auto y crece con el contenido en lugar de activar el scroll,
             quedando recortado por el overflow-hidden del panel. -->
        <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-6">
          <slot />
        </div>

        <!-- PIE (opcional) -->
        <div
          v-if="$slots.footer"
          class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 flex flex-wrap items-center justify-end gap-3 shrink-0"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style>
/* Transición de apertura/cierre del overlay (misma estética que el resto del app). */
.ui-modal-enter-active { transition: opacity 0.2s ease; }
.ui-modal-enter-from, .ui-modal-leave-to { opacity: 0; }
.ui-modal-leave-active { transition: opacity 0.15s ease; }

/* Scrollbar consistente para el cuerpo del modal. */
.ui-modal-panel .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
.ui-modal-panel .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
.ui-modal-panel .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
</style>